import { useEffect } from "react";

/**
 * ⌘K MAC'TE, CTRL K ÖTEKİLERDE — 1 Kasım 2026, kullanıcı isteği: «Windows'ta Command tuşu yok».
 *
 * Kitin paleti kısayolu kendisi dinliyordu ve iki değiştiriciyi de HER platformda kabul ediyordu.
 * Mac'te Ctrl+K da paleti açıyordu; oysa Mac'te Ctrl+K bir metin kutusunda satırın sonunu siler
 * (macOS'un Emacs kısayolu) ve form dolduran kişinin elinden alınıyordu. Windows'ta ⌘'nin yerindeki
 * tuş Windows tuşu, Win+K de işletim sisteminin. Artık kısayol platformun KENDİ değiştiricisi:
 * Mac'te ⌘K, Windows ve Linux'ta Ctrl+K. Kitin dinleyicisi kapalı (`shortcut={false}`); tek
 * dinleyici burada, çünkü iki dinleyici paleti aynı tuşta açıp kapatırdı.
 *
 * İPUCU KİTTE. Üst çubuktaki «⌘ K» / «Ctrl K» kitin `AppNavbar`'ı kendi platform okumasıyla
 * çiziyor (`navigator.platform` + `userAgent`). Buradaki okuma önce `userAgentData.platform`'a
 * bakıyor. Gerçek tarayıcıların hepsinde iki okuma aynı cevabı veriyor; Windows'u taklit eden
 * sınama ikisini birlikte ölçüyor (`palette-shortcut.test.tsx`).
 */

/** Platformu söyleyen iki alan — `userAgentData` TypeScript'in DOM tiplerinde henüz yok. */
export interface PlatformHints {
  platform?: string | undefined;
  userAgentData?: { platform?: string | undefined } | undefined;
}

/**
 * Apple klavyesi mi: Mac, iPad ve iPhone (klavyeli iPad ⌘ kullanıyor; iPadOS kendini «MacIntel»
 * diye de tanıtıyor). Önce `userAgentData.platform` («macOS», «Windows», «Linux»), yoksa
 * `navigator.platform` («MacIntel», «Win32», «Linux x86_64»). Safari ve Firefox `userAgentData`
 * vermiyor; orada eski alan tek kaynak.
 */
export function isApplePlatform(
  hints: PlatformHints | undefined = typeof navigator === "undefined" ? undefined : navigator,
): boolean {
  if (!hints) return false;
  const hinted = hints.userAgentData?.platform;
  const name = hinted && hinted !== "Unknown" ? hinted : (hints.platform ?? "");
  return /mac|iphone|ipad|ipod|ios/i.test(name);
}

/**
 * Bir kez, modül açılırken: makinenin klavyesi oturum boyunca değişmiyor. `typeof navigator`
 * koruması jsdom ve sunucu tarafı için — orada nesne yok ve çıplak okuma modül yüklenirken patlar.
 */
export const IS_APPLE_PLATFORM = isApplePlatform();

/** Paletin kısayolu: görünen tuşlar ve ARIA'nın sözlüğüyle aynı kısayol (`aria-keyshortcuts`). */
export interface PaletteShortcut {
  keys: readonly [modifier: string, key: string];
  aria: string;
}

export function paletteShortcutFor(apple: boolean): PaletteShortcut {
  return apple ? { keys: ["⌘", "K"], aria: "Meta+K" } : { keys: ["Ctrl", "K"], aria: "Control+K" };
}

/**
 * Bu tuş vuruşu paletin kısayolu mu: yalnız platformun değiştiricisi ve K.
 *
 * Shift ya da Alt eklenmişse DEĞİL: Firefox'ta Ctrl+Shift+K web konsolu, ⌘⇧K başka uygulamaların.
 * Basılı tutulan tuşun tekrarı da değil: palet her tekrarda açılıp kapanırdı. IME bileşimi sürerken
 * (`isComposing`) tuş o bileşimin. `toLowerCase()`, `toLocaleLowerCase("tr-TR")` değil: tuş
 * kimliği metin değil (kitteki gerekçe).
 */
export function isPaletteShortcut(
  event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey" | "repeat" | "isComposing">,
  apple: boolean,
): boolean {
  if (event.isComposing || event.repeat) return false;
  if (event.key.toLowerCase() !== "k") return false;
  if (event.altKey || event.shiftKey) return false;
  return apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

/**
 * Kısayolun tek dinleyicisi: paleti açar, açıksa kapatır.
 *
 * `preventDefault` işin kendisi: Chrome ve Edge Ctrl+K'yı adres çubuğunda aramaya, Firefox arama
 * kutusuna veriyor. Sayfa tuşu almazsa Windows'ta palet açılır açılmaz odak adres çubuğuna gider.
 */
export function usePaletteShortcut(
  setOpen: (update: (open: boolean) => boolean) => void,
  apple: boolean = IS_APPLE_PLATFORM,
): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isPaletteShortcut(event, apple)) return;
      event.preventDefault();
      setOpen((open) => !open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen, apple]);
}
